// Zentrale Daten-/API-Schicht der Wartungen-Welt (React-Port von frontend/wartungen/*).
// Alle Pfade sind relativ zur API-Base (siehe apiClient). In der Legacy-Welt lautete
// der Präfix `${API_URL}` – hier entspricht das den gleichen Endpunkten ohne Präfix.

import { api, API_BASE, ApiError } from "@/lib/apiClient";

/**
 * Backend-Domains (= erstes URL-Segment der Wartungs-Endpunkte), für die das
 * Realtime-Middleware Events sendet. Wird von den Wartungs-Seiten via
 * `useRealtimeSync` abonniert, damit Änderungen anderer Benutzer still
 * nachgeladen werden.
 */
export const WARTUNGEN_REALTIME_DOMAINS = [
  "maintenance-contracts",
  "maintenance-campaigns",
  "maintenance-events",
  "maintenance-types",
  "maintenance",
  "forms",
] as const;

/* ──────────────────────────────────────────────────────────────────────────
 * Typen
 * ──────────────────────────────────────────────────────────────────────── */

export interface MaintenanceType {
  id?: number;
  key: string;
  name?: string;
  name_singular?: string;
  name_plural?: string;
  [k: string]: unknown;
}

/** Im InfoHub (SharePoint) hinterlegtes Vertragsdokument (PDF). */
export interface MaintenanceContractDocument {
  filename: string;
  content_type?: string | null;
  size_bytes?: number | null;
  sharepoint_item_id?: string | null;
  web_url?: string | null;
  folder_path?: string | null;
  uploaded_by?: string | null;
  uploaded_at?: string | null;
}

/** `contract` = Wartungsvertrag, `on_call` = Wartung auf Zuruf ohne Vertrag. */
export type MaintenanceContractBasis = "contract" | "on_call";

export function isOnCallContract(contract: { basis?: string | null } | null | undefined): boolean {
  return contract?.basis === "on_call";
}

export interface MaintenanceContract {
  id: number;
  /** Fehlt bei älteren Antworten; dann gilt der Eintrag als Wartungsvertrag. */
  basis?: MaintenanceContractBasis | null;
  hero_customer_id?: number | null;
  hero_customer_name?: string | null;
  /** Frei wählbarer Anzeigename (z. B. „Klinikum Nord – Brandschutztüren“). */
  name?: string | null;
  contract_number?: string | null;
  category?: string | null;
  status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  interval_value?: number | null;
  interval_unit?: string | null;
  auto_renew_enabled?: boolean | null;
  notice_period_value?: number | null;
  notice_period_unit?: string | null;
  renewal_term_value?: number | null;
  renewal_term_unit?: string | null;
  sub_location_plan_placement_required?: boolean | null;
  hero_address_ids?: number[];
  locations?: ContractLocation[];
  maintenance_type_keys?: string[];
  maintenance_types?: { key: string; name?: string }[];
  customer_name?: string | null;
  customer_geschaeftspartner_id?: number | null;
  customer_has_logo?: boolean | null;
  customer_logo_updated_at?: string | null;
  form_mappings?: ContractFormMapping[];
  document?: MaintenanceContractDocument | null;
  [k: string]: unknown;
}

export interface ContractLocation {
  hero_address_id?: number;
  id?: number;
  location_name?: string | null;
  name?: string | null;
  label?: string | null;
  [k: string]: unknown;
}

export interface ContractFormMapping {
  maintenance_type_key: string;
  form_id?: number | string | null;
  hero_address_id?: number | null;
  [k: string]: unknown;
}

export interface MaintenanceCampaignProgress {
  total_assets?: number | null;
  completed_assets?: number | null;
}

export interface MaintenanceCampaign {
  id: number;
  name?: string | null;
  status?: string | null;
  date_mode?: string | null;
  date_value?: string | null;
  maintenance_type_id?: number | null;
  maintenance_type_key?: string | null;
  project_id?: number | null;
  contract_id?: number | null;
  hero_address_id?: number | null;
  /** Reiner Ortsname (Stadt): COALESCE(orte.ort, orte.name). */
  location_name?: string | null;
  /** Titel des Ortes (orte.name) – wie in der iOS-App angezeigt. */
  building_name?: string | null;
  /** Unterort(e) der Kampagne (z. B. LOG, P1, UZ), falls gesetzt. */
  sub_location_name?: string | null;
  /** Kampagnen-Scope: nur diese Unterort-IDs gehören zur Wartung. */
  sub_location_ids?: number[];
  customer_name?: string | null;
  maintenance_type_name?: string | null;
  campaign_id?: number | null;
  year?: number | null;
  progress?: MaintenanceCampaignProgress | null;
  /** Workflow-Status des verknüpften Projekts (`auftraege.workflow_status`). */
  workflow_status?: string | null;
  workflow_status_label?: string | null;
  project_completed?: boolean | null;
  [k: string]: unknown;
}

export function maintenanceCampaignProgress(
  campaign: MaintenanceCampaign | null | undefined,
): { completed: number; total: number; percent: number } | null {
  const progress = campaign?.progress;
  if (!progress || typeof progress !== "object") return null;
  const total = Number(progress.total_assets);
  const completed = Number(progress.completed_assets);
  if (!Number.isFinite(total) || total <= 0) return null;
  const safeCompleted = Number.isFinite(completed) ? Math.min(Math.max(0, completed), total) : 0;
  return {
    completed: safeCompleted,
    total,
    percent: Math.round((safeCompleted / total) * 100),
  };
}

export interface MaintenanceProject {
  id?: number;
  project_id?: number;
  auftragsnummer?: string | null;
  beschreibung?: string | null;
  kunde?: string | null;
  customer_name?: string | null;
  ort?: string | null;
  location_name?: string | null;
  hero_address_id?: number | null;
  [k: string]: unknown;
}

export interface FieldTemplate {
  id: number;
  template_key?: string;
  field_type?: string;
  default_label?: string;
  category_hint?: string | null;
  scope?: string | null;
  default_options_json?: Record<string, unknown> | null;
  semantic_role?: string | null;
  renderable?: boolean;
  [k: string]: unknown;
}

export interface BuilderSchemaField {
  field_id?: number | null;
  id?: number | null;
  field_key?: string;
  question_key?: string;
  label?: string;
  question_label?: string;
  type?: string;
  field_type?: string;
  scope?: string | null;
  scope_override?: string | null;
  /** Aufgelöster Scope aus dem Builder-Schema (COALESCE(scope_override, template.scope)). */
  effective_scope?: string | null;
  category?: string | null;
  required?: boolean;
  required_override?: boolean | null;
  options_override_json?: Record<string, unknown> | null;
  ui_override_json?: Record<string, unknown> | null;
  behavior_override_json?: Record<string, unknown> | null;
  semantic_role?: string | null;
  semantic_role_override?: string | null;
  /** Aufgelöste Rolle aus dem Builder-Schema (Override vor Template). */
  effective_semantic_role?: string | null;
  template_id?: number | null;
  sort_order?: number | null;
  layout_row?: number | null;
  layout_col?: number | null;
  layout_span?: number | null;
  layout_weight?: number | null;
  [k: string]: unknown;
}

export interface BuilderSchemaResponse {
  form_id?: number | string;
  form_name?: string;
  name?: string;
  fields?: BuilderSchemaField[];
  grade_config_json?: { enabled_grades?: number[] } | null;
  plan_symbols?: PlanSymbol[];
  [k: string]: unknown;
}

export interface FormListEntry {
  form_id: number | string;
  id?: number | string;
  name?: string | null;
  status?: string | null;
  is_default?: boolean;
  is_published?: boolean;
  field_count?: number | null;
  updated_at?: string | null;
  [k: string]: unknown;
}

export interface PlanSymbol {
  id?: number | null;
  sort_order?: number;
  title?: string | null;
  svg_markup?: string;
  png_preview_base64?: string;
  png_base64?: string;
  visibility_behavior_json?: Record<string, unknown>;
  svg_placeholder_bindings_json?: unknown;
  [k: string]: unknown;
}

export interface MaterialSuggestion {
  id?: number | string;
  artikel_id?: number | string;
  hero_nr?: string;
  label?: string;
  name?: string;
  bezeichnung?: string;
  [k: string]: unknown;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Maintenance-Typen / Kategorien
 * ──────────────────────────────────────────────────────────────────────── */

export async function listMaintenanceTypes(): Promise<MaintenanceType[]> {
  const data = await api.get<unknown>("/maintenance-types");
  if (Array.isArray(data)) return data as MaintenanceType[];
  if (data && typeof data === "object" && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: MaintenanceType[] }).items;
  }
  return [];
}

export async function createMaintenanceType(payload: {
  key: string;
  name_singular: string;
  name_plural: string;
}): Promise<MaintenanceType> {
  return api.post<MaintenanceType>("/maintenance-types", payload);
}

export async function updateMaintenanceType(
  key: string,
  payload: { name_singular: string; name_plural: string },
): Promise<MaintenanceType> {
  return api.patch<MaintenanceType>(`/maintenance-types/${encodeURIComponent(key)}`, payload);
}

export function maintenanceTypeLabel(t: MaintenanceType | undefined | null): string {
  if (!t) return "";
  return (t.name_plural || t.name || t.name_singular || t.key || "").toString();
}

export function maintenanceTypeSingular(t: MaintenanceType | undefined | null): string {
  if (!t) return "";
  return (t.name_singular || t.name || t.name_plural || t.key || "").toString();
}

export interface RecentMaintenanceEvent {
  asset_id?: number | string;
  asset_name?: string | null;
  maintenance_event_id?: number | string;
  type?: string | null;
  status?: string | null;
  executed_at?: string | null;
  created_at?: string | null;
  date?: string | null;
  performed_by_name?: string | null;
  performed_by?: string | null;
  user?: string | null;
  [k: string]: unknown;
}

export async function getRecentMaintenanceEvents(
  categoryKey: string,
  limit = 20,
): Promise<RecentMaintenanceEvent[]> {
  const data = await api.get<unknown>(
    `/maintenance-types/${encodeURIComponent(categoryKey)}/maintenance-events/recent?limit=${limit}`,
  );
  if (Array.isArray(data)) return data as RecentMaintenanceEvent[];
  if (data && typeof data === "object" && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: RecentMaintenanceEvent[] }).items;
  }
  return [];
}

/* ──────────────────────────────────────────────────────────────────────────
 * Verträge
 * ──────────────────────────────────────────────────────────────────────── */

export interface ListContractsParams {
  limit?: number;
  hero_customer_id?: string | number;
  category?: string;
  status?: string;
  location_query?: string;
  auto_renew_enabled?: string;
}

export async function listMaintenanceContracts(
  params: ListContractsParams = {},
): Promise<MaintenanceContract[]> {
  const usp = new URLSearchParams();
  if (params.limit) usp.set("limit", String(params.limit));
  if (params.hero_customer_id) usp.set("hero_customer_id", String(params.hero_customer_id));
  if (params.category) usp.set("category", params.category);
  if (params.status) usp.set("status", params.status);
  if (params.location_query) usp.set("location_query", params.location_query);
  if (params.auto_renew_enabled) usp.set("auto_renew_enabled", params.auto_renew_enabled);
  const qs = usp.toString();
  const data = await api.get<{ contracts?: MaintenanceContract[] }>(
    `/maintenance-contracts${qs ? `?${qs}` : ""}`,
  );
  return data?.contracts ?? [];
}

export async function getMaintenanceContract(id: number | string): Promise<MaintenanceContract> {
  return api.get<MaintenanceContract>(`/maintenance-contracts/${encodeURIComponent(String(id))}`);
}

export interface HeroCustomerEntry {
  id: number | string;
  company_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  [k: string]: unknown;
}

export function heroCustomerLabel(entry: HeroCustomerEntry | null | undefined): string {
  if (!entry) return "";
  const company = String(entry.company_name || "").trim();
  const name = `${String(entry.first_name || "").trim()} ${String(entry.last_name || "").trim()}`.trim();
  return company || name || String(entry.id || "").trim();
}

export async function searchHeroCustomers(q: string): Promise<HeroCustomerEntry[]> {
  const query = String(q || "").trim();
  if (!query) return [];
  const data = await api.get<unknown>(`/hero/kunden?q=${encodeURIComponent(query)}`);
  return Array.isArray(data) ? (data as HeroCustomerEntry[]) : [];
}

export interface HeroCustomerAddress {
  address_id: number | string;
  title?: string | null;
  street?: string | null;
  city?: string | null;
  deleted?: boolean;
  [k: string]: unknown;
}

export function heroAddressLabel(entry: HeroCustomerAddress | null | undefined): string {
  if (!entry) return "";
  return `${entry.title ?? "Unbenannt"} – ${entry.street ?? ""}, ${entry.city ?? ""}`.trim();
}

export function contractLocationLabel(
  loc: ContractLocation | null | undefined,
  heroAddressIdFallback?: number | string | null,
): string {
  const label = String(loc?.location_name || loc?.name || loc?.label || "").trim();
  if (label) return label;
  const id = Number(loc?.hero_address_id ?? loc?.id ?? heroAddressIdFallback);
  return Number.isFinite(id) && id > 0 ? `Ort #${id}` : "";
}

export function maintenanceProjectCustomerLabel(project: MaintenanceProject | null | undefined): string {
  return String(project?.customer_name || project?.kunde || "").trim();
}

export function maintenanceProjectLocationLabel(project: MaintenanceProject | null | undefined): string {
  return String(project?.location_name || project?.ort || "").trim();
}

/** Kopfzeile in Projektlisten: Auftragsnummer mit Kundenname (nicht nur ID). */
export function maintenanceProjectHeadline(project: MaintenanceProject | null | undefined): string {
  if (!project) return "";
  const num = String(project.auftragsnummer || "").trim();
  const customer = maintenanceProjectCustomerLabel(project);
  if (num && customer) return `${num} · ${customer}`;
  return num || customer || `Projekt ${project.project_id ?? project.id ?? ""}`;
}

/** Kundenname für Verträge – bevorzugt aufgelösten Namen, nicht nur Hero-ID. */
export function maintenanceContractCustomerLabel(
  contract: MaintenanceContract | null | undefined,
): string {
  if (!contract) return "";
  const name = String(contract.hero_customer_name || contract.customer_name || "").trim();
  if (name) return name;
  const id = contract.hero_customer_id;
  if (id != null && String(id).trim() !== "") return `Kunde #${id}`;
  return "";
}

export async function enrichContractLocations(
  locations: ContractLocation[],
  heroCustomerId?: number | string | null,
): Promise<ContractLocation[]> {
  const normalized = Array.isArray(locations) ? locations.filter(Boolean) : [];
  if (normalized.length === 0) return [];

  const needsEnrichment = normalized.some(
    (loc) => !String(loc.location_name || loc.name || loc.label || "").trim(),
  );
  if (!needsEnrichment) return normalized;

  const customerId = Number(heroCustomerId);
  if (!Number.isFinite(customerId) || customerId <= 0) return normalized;

  try {
    const addresses = await getHeroCustomerAddresses(customerId);
    const byId = new Map(
      addresses
        .filter((addr) => !addr.deleted)
        .map((addr) => [String(addr.address_id), heroAddressLabel(addr)]),
    );
    return normalized.map((loc) => {
      const existing = String(loc.location_name || loc.name || loc.label || "").trim();
      if (existing) return loc;
      const heroId = String(loc.hero_address_id ?? "");
      const resolved = byId.get(heroId);
      return resolved ? { ...loc, location_name: resolved } : loc;
    });
  } catch {
    return normalized;
  }
}

export async function getHeroCustomerAddresses(
  heroCustomerId: number | string,
  q = "",
): Promise<HeroCustomerAddress[]> {
  const id = String(heroCustomerId || "").trim();
  if (!id) return [];
  const search = q ? `?q=${encodeURIComponent(q)}` : "";
  const data = await api.get<unknown>(`/hero/customer-addresses/${encodeURIComponent(id)}${search}`);
  return Array.isArray(data) ? (data as HeroCustomerAddress[]) : [];
}

export interface ContractFormOption {
  form_id: number | string;
  form_name?: string | null;
  maintenance_type_key?: string | null;
  is_default?: boolean;
  [k: string]: unknown;
}

export interface ContractOptions {
  maintenance_types?: MaintenanceType[];
  forms?: ContractFormOption[];
  [k: string]: unknown;
}

export async function getContractOptions(): Promise<ContractOptions> {
  return api.get<ContractOptions>("/maintenance-contracts/options");
}

export async function saveMaintenanceContract(
  payload: Record<string, unknown>,
  id?: number | string | null,
): Promise<MaintenanceContract> {
  if (id) {
    return api.put<MaintenanceContract>(
      `/maintenance-contracts/${encodeURIComponent(String(id))}`,
      payload,
    );
  }
  return api.post<MaintenanceContract>("/maintenance-contracts", payload);
}

export async function deleteMaintenanceContract(id: number | string): Promise<void> {
  await api.del(`/maintenance-contracts/${encodeURIComponent(String(id))}`);
}

/* ───────────── Vertragsdokument (PDF im InfoHub) ───────────── */

export async function uploadMaintenanceContractDocument(
  contractId: number | string,
  file: File,
): Promise<{ id: number; document: MaintenanceContractDocument | null }> {
  const form = new FormData();
  form.append("file", file, file.name);
  return api.postForm(`/maintenance-contracts/${encodeURIComponent(String(contractId))}/document`, form);
}

export async function deleteMaintenanceContractDocument(
  contractId: number | string,
): Promise<{ id: number; document: null; warnings?: string[] }> {
  return api.del(`/maintenance-contracts/${encodeURIComponent(String(contractId))}/document`);
}

/** URL, unter der die API das Vertrags-PDF ausliefert (inline im Browser oder als Download). */
export function maintenanceContractDocumentUrl(
  contractId: number | string,
  options: { download?: boolean } = {},
): string {
  const base = `${API_BASE}/maintenance-contracts/${encodeURIComponent(String(contractId))}/document/file`;
  return options.download ? `${base}?download=true` : base;
}

/**
 * Öffnet das Vertrags-PDF in einem neuen Tab bzw. lädt es herunter.
 * Direkte `<a href>`-Links auf die API blockiert das Client-Gate (Top-Level-
 * Navigation), deshalb wird die Datei per fetch geladen und als Blob geöffnet.
 */
export async function openMaintenanceContractDocument(
  contractId: number | string,
  options: { download?: boolean; filename?: string } = {},
): Promise<void> {
  const res = await fetch(maintenanceContractDocumentUrl(contractId), { credentials: "include" });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = (await res.json()) as { detail?: unknown };
      if (typeof parsed?.detail === "string" && parsed.detail.trim()) message = parsed.detail.trim();
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const filename = options.filename || `Wartungsvertrag-${contractId}.pdf`;
  const opened = options.download ? null : window.open(url, "_blank", "noopener");
  if (!opened) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function getContractCampaigns(
  contractId: number | string,
): Promise<MaintenanceCampaign[]> {
  const data = await api.get<{ campaigns?: MaintenanceCampaign[] }>(
    `/maintenance/contracts/${encodeURIComponent(String(contractId))}/overview?mode=campaigns`,
  );
  return data?.campaigns ?? [];
}

export interface ContractProjectsResult {
  contract?: MaintenanceContract;
  projects?: MaintenanceProject[];
}

export async function getContractProjects(
  contractId: number | string,
  opts: { limit?: number; q?: string; hero_address_id?: number | string } = {},
): Promise<ContractProjectsResult> {
  const usp = new URLSearchParams();
  if (opts.limit) usp.set("limit", String(opts.limit));
  if (opts.q) usp.set("q", opts.q);
  if (opts.hero_address_id) usp.set("hero_address_id", String(opts.hero_address_id));
  const qs = usp.toString();
  return api.get<ContractProjectsResult>(
    `/maintenance/contracts/${encodeURIComponent(String(contractId))}/projects${qs ? `?${qs}` : ""}`,
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Kampagnen (Wartungshüllen)
 * ──────────────────────────────────────────────────────────────────────── */

export interface CreateCampaignPayload {
  maintenance_type_id?: number | null;
  project_id: number;
  contract_id: number;
  name: string;
  date_mode: string;
  date_value: string;
  status?: string;
  hero_address_id?: number | null;
}

export async function createMaintenanceCampaign(
  payload: CreateCampaignPayload,
): Promise<{ campaign: { id: number } }> {
  return api.post<{ campaign: { id: number } }>("/maintenance-campaigns", {
    status: "draft",
    ...payload,
  });
}

export async function updateMaintenanceCampaignName(
  campaignId: number,
  name: string,
): Promise<{ campaign: MaintenanceCampaign }> {
  return api.patch<{ campaign: MaintenanceCampaign }>(
    `/maintenance-campaigns/${encodeURIComponent(String(campaignId))}`,
    { name },
  );
}

export interface CampaignScopedSubLocation {
  sub_location_id?: number;
  name?: string | null;
  parent_id?: number | null;
  sort_order?: number;
  active?: boolean;
}

export interface MaintenanceCampaignAssetRow {
  asset_id?: number;
  external_id?: string | null;
  asset_name?: string | null;
  asset_status?: string | null;
  sub_location_id?: number | null;
  sub_location_name?: string | null;
  maintenance_event_id?: number | null;
  result_status?: string | null;
  grade_final?: number | null;
  is_completed?: boolean;
  /** Wartungstermin des Events dieser Kampagne (fachlich, ggf. nur Jahr → 1.1.). */
  performed_at?: string | null;
  /** Zeitpunkt des Formularabschlusses. */
  completed_at?: string | null;
  /** Letzter technischer Speicherzeitpunkt. */
  updated_at?: string | null;
  /** Globale Wartungshistorie (neueste zuerst, ohne offene Vorgänge). */
  recent_maintenances?: RecentMaintenanceEntry[];
  current_maintenance_has_form_data?: boolean;
  current_maintenance_started?: boolean;
  plan_label_symbol?: Record<string, unknown> | null;
  masterdata?: Record<string, unknown>;
  [k: string]: unknown;
}

export interface RecentMaintenanceEntry {
  maintenance_event_id?: number | null;
  maintenance_type_key?: string | null;
  maintenance_type_name?: string | null;
  result_status?: string | null;
  grade_final?: number | null;
  performed_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  [k: string]: unknown;
}

export interface MaintenanceCampaignAssetsResult {
  campaign?: Record<string, unknown>;
  sub_locations?: Array<{
    sub_location_id?: number | null;
    sub_location_name?: string | null;
    assets?: MaintenanceCampaignAssetRow[];
  }>;
  /** Nur bei compact=true: Kampagnen-Ortsbaum inkl. parent_id. */
  scoped_sub_locations?: CampaignScopedSubLocation[];
  /** Nur bei compact=true. */
  require_plan_placement?: boolean;
  [k: string]: unknown;
}

export async function getMaintenanceCampaign(
  campaignId: number | string,
): Promise<{
  campaign?: Record<string, unknown>;
  sub_locations?: CampaignScopedSubLocation[];
  assets?: Record<string, unknown>;
}> {
  return api.get(`/maintenance-campaigns/${encodeURIComponent(String(campaignId))}`);
}

export async function getMaintenanceCampaignAssets(
  campaignId: number | string,
  opts: { compact?: boolean } = {},
): Promise<MaintenanceCampaignAssetsResult> {
  const usp = new URLSearchParams();
  if (opts.compact) usp.set("compact", "true");
  const qs = usp.toString();
  return api.get(
    `/maintenance-campaigns/${encodeURIComponent(String(campaignId))}/assets${qs ? `?${qs}` : ""}`,
  );
}

/* ─────────────────────── Auswertung einer Wartung (Kampagne) ─────────────────── */

export interface CampaignEvaluationEvent {
  maintenance_event_id: number;
  form_id?: number | null;
  result_status?: string | null;
  is_completed?: boolean;
  grade_auto?: number | null;
  grade_manual?: number | null;
  grade_final?: number | null;
  grade_override_reason?: string | null;
  performed_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
  performed_by_user_id?: string | null;
  performed_by_display_name?: string | null;
  /** Prüfantworten (`maintenance_event.data_json`) ohne Binärdaten. */
  data?: Record<string, unknown>;
}

export interface CampaignEvaluationAsset {
  asset_id: number;
  external_id?: string | null;
  asset_name?: string | null;
  asset_status?: string | null;
  sub_location_id?: number | null;
  sub_location_name?: string | null;
  masterdata?: Record<string, unknown>;
  /** Aktuelles Wartungsereignis dieser Kampagne – `null`, wenn noch nicht gestartet. */
  event?: CampaignEvaluationEvent | null;
}

export interface CampaignEvaluationResult {
  campaign?: Record<string, unknown>;
  form?: {
    form_id?: number | null;
    form_ids?: number[];
    grade_config_json?: { enabled_grades?: number[] } | null;
    fields?: RuntimeFormField[];
  };
  assets?: CampaignEvaluationAsset[];
}

export async function getMaintenanceCampaignEvaluation(
  campaignId: number | string,
): Promise<CampaignEvaluationResult> {
  return api.get<CampaignEvaluationResult>(
    `/maintenance-campaigns/${encodeURIComponent(String(campaignId))}/evaluation`,
  );
}

/** Pro Benutzer und Wartungsart gespeicherte Fragenauswahl der Auswertung. */
export function evaluationQuestionSettingsKey(typeKey: string): string {
  return `wartung-auswertung-fragen:${String(typeKey || "").trim() || "default"}`;
}

export async function getEvaluationQuestionSettings(typeKey: string): Promise<string[] | null> {
  const res = await api.get<HuelleColumnSettingsResponse>(
    `/me/table-settings/${encodeURIComponent(evaluationQuestionSettingsKey(typeKey))}`,
  );
  const columns = res?.settings?.desktop?.columns;
  return Array.isArray(columns) ? columns.map((c) => String(c)) : null;
}

export async function saveEvaluationQuestionSettings(typeKey: string, fieldKeys: string[]): Promise<void> {
  await api.post(`/me/table-settings/${encodeURIComponent(evaluationQuestionSettingsKey(typeKey))}`, {
    settings: { desktop: { columns: fieldKeys, widths: {} }, mobile: { columns: fieldKeys, widths: {} } },
  });
}

/* ─────────────────────── Tabellen-Settings (Wartungshülle) ─────────────────── */

export const HUELLE_TABLE_KEY = "wartung-huelle";

export interface HuelleColumnView {
  columns: string[];
  widths: Record<string, number>;
}

export interface HuelleColumnSettings {
  desktop: HuelleColumnView | null;
  mobile: HuelleColumnView | null;
}

interface HuelleColumnSettingsResponse {
  table_key: string;
  settings: HuelleColumnSettings | null;
}

export async function getHuelleColumnSettings(): Promise<HuelleColumnSettings> {
  const res = await api.get<HuelleColumnSettingsResponse>(
    `/me/table-settings/${HUELLE_TABLE_KEY}`,
  );
  const settings = res?.settings ?? null;
  return {
    desktop: settings?.desktop ?? null,
    mobile: settings?.mobile ?? null,
  };
}

export async function saveHuelleColumnSettings(
  settings: HuelleColumnSettings,
): Promise<HuelleColumnSettings> {
  const res = await api.post<{ ok: true; settings: HuelleColumnSettings }>(
    `/me/table-settings/${HUELLE_TABLE_KEY}`,
    { settings },
  );
  return {
    desktop: res?.settings?.desktop ?? null,
    mobile: res?.settings?.mobile ?? null,
  };
}

export function flattenMaintenanceCampaignAssets(
  result: MaintenanceCampaignAssetsResult,
): MaintenanceCampaignAssetRow[] {
  const groups = Array.isArray(result.sub_locations) ? result.sub_locations : [];
  const rows: MaintenanceCampaignAssetRow[] = [];
  for (const group of groups) {
    const subLocationId = group.sub_location_id ?? null;
    const subLocationName = group.sub_location_name ?? null;
    for (const asset of group.assets || []) {
      rows.push({
        ...asset,
        sub_location_id: asset.sub_location_id ?? subLocationId,
        sub_location_name: asset.sub_location_name ?? subLocationName,
      });
    }
  }
  return rows;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Formulare
 * ──────────────────────────────────────────────────────────────────────── */

export async function listForms(categoryKey: string): Promise<FormListEntry[]> {
  const data = await api.get<{ forms?: FormListEntry[] }>(
    `/forms/${encodeURIComponent(categoryKey)}/list`,
  );
  return data?.forms ?? [];
}

export async function createEmptyDraftForm(
  categoryKey: string,
  name: string,
): Promise<{ form_id: number | string }> {
  return api.post<{ form_id: number | string }>(
    `/forms/${encodeURIComponent(categoryKey)}/draft-empty`,
    { name },
  );
}

export async function setFormDefault(formId: number | string): Promise<void> {
  await api.post(`/forms/${encodeURIComponent(String(formId))}/set-default`);
}

export async function getBuilderSchema(formId: number | string): Promise<BuilderSchemaResponse> {
  try {
    return await api.get<BuilderSchemaResponse>(
      `/forms/${encodeURIComponent(String(formId))}/builder/schema`,
    );
  } catch {
    // Fallback auf das veröffentlichte/normale Schema, wenn Builder-Schema (404) fehlt.
    return api.get<BuilderSchemaResponse>(`/forms/${encodeURIComponent(String(formId))}/schema`);
  }
}

export interface SaveBuilderSchemaPayload {
  fields: Record<string, unknown>[];
  deleted_field_keys: string[];
  renamed_field_keys: { from: string; to: string }[];
  grade_config_json: { enabled_grades: number[] };
}

export async function saveBuilderSchema(
  formId: number | string,
  payload: SaveBuilderSchemaPayload,
): Promise<void> {
  await api.put(`/forms/${encodeURIComponent(String(formId))}/builder/schema`, payload);
}

export async function publishForm(formId: number | string): Promise<void> {
  await api.post(`/forms/${encodeURIComponent(String(formId))}/publish`);
}

/* ──────────────────────────────────────────────────────────────────────────
 * Field-Templates / Plan-Symbole / Material
 * ──────────────────────────────────────────────────────────────────────── */

export async function listFieldTemplates(): Promise<FieldTemplate[]> {
  const data = await api.get<unknown>("/field-templates");
  if (Array.isArray(data)) return data as FieldTemplate[];
  if (data && typeof data === "object" && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: FieldTemplate[] }).items;
  }
  return [];
}

export async function getPlanSymbols(categoryKey: string): Promise<PlanSymbol[]> {
  const data = await api.get<{ plan_symbols?: PlanSymbol[] }>(
    `/maintenance-types/${encodeURIComponent(categoryKey)}/plan-symbols`,
  );
  return data?.plan_symbols ?? [];
}

export async function savePlanSymbols(
  categoryKey: string,
  planSymbols: Record<string, unknown>[],
): Promise<void> {
  await api.put(`/maintenance-types/${encodeURIComponent(categoryKey)}/plan-symbols`, {
    plan_symbols: planSymbols,
  });
}

/* ──────────────────────────────────────────────────────────────────────────
 * Formular-Laufzeit (Wartungsformular)
 * ──────────────────────────────────────────────────────────────────────── */

export interface RuntimeFormField {
  field_key?: string;
  key?: string;
  label?: string;
  label_override?: string;
  effective_label?: string;
  type?: string;
  field_type?: string;
  effective_field_type?: string;
  scope?: string;
  effective_scope?: string;
  resolved_scope?: string;
  category?: string | null;
  value?: unknown;
  resolved_visible?: boolean;
  resolved_enabled?: boolean;
  resolved_required?: boolean;
  required?: boolean;
  effective_options?: Record<string, unknown> | unknown[];
  options?: Record<string, unknown> | unknown[];
  options_override_json?: Record<string, unknown>;
  runtime?: Record<string, unknown>;
  ui?: Record<string, unknown>;
  [k: string]: unknown;
}

export interface EffectiveFormSchema {
  form_id?: number;
  template_id?: number | null;
  form_fields?: RuntimeFormField[];
  fields?: RuntimeFormField[];
  schema?: { fields?: RuntimeFormField[] };
  grade_config_json?: { enabled_grades?: number[] } | null;
  published?: { form_id?: number; template_id?: number | null; resolution_source?: string };
  [k: string]: unknown;
}

export interface MaintenanceEventContext {
  type_key?: string | null;
  form_id?: number | null;
  asset_id?: number | null;
  contract_id?: number | null;
  hero_address_id?: number | null;
  grade_manual?: string | number | null;
  grade_override_reason?: string | null;
  linked_assets?: Record<string, unknown>[];
  project?: { hero_address_id?: number | null };
  project_completed?: boolean | null;
  is_completed_event?: boolean | null;
  editable?: boolean | null;
  details?: Record<string, unknown>;
  [k: string]: unknown;
}

export interface AssetSummary {
  external_id?: string | null;
  name?: string | null;
  last_performed_at?: string | null;
  [k: string]: unknown;
}

export async function getMaintenanceEventContext(eventId: number | string): Promise<MaintenanceEventContext> {
  return api.get<MaintenanceEventContext>(`/maintenance-events/${encodeURIComponent(String(eventId))}/context`);
}

export async function getPublishedFormConfig(opts: {
  typeKey: string;
  formId?: number | null;
  contractId?: number | null;
  heroAddressId?: number | null;
}): Promise<EffectiveFormSchema> {
  let published: { form_id?: number; template_id?: number | null; resolution_source?: string } | null = null;
  let resolvedFormId = opts.formId ? Number(opts.formId) : null;

  if (!resolvedFormId && opts.typeKey) {
    const usp = new URLSearchParams();
    if (opts.contractId && Number(opts.contractId) > 0) usp.set("contract_id", String(opts.contractId));
    if (opts.heroAddressId && Number(opts.heroAddressId) > 0) usp.set("hero_address_id", String(opts.heroAddressId));
    const qs = usp.toString();
    published = await api.get<{ form_id?: number }>(
      `/forms/${encodeURIComponent(opts.typeKey)}/published${qs ? `?${qs}` : ""}`,
    );
    resolvedFormId = Number(published?.form_id);
  }

  if (!Number.isFinite(resolvedFormId) || (resolvedFormId ?? 0) <= 0) {
    throw new Error("Keine gültige form_id für das Wartungsformular verfügbar.");
  }

  const schema = await api.get<EffectiveFormSchema>(
    `/forms/${encodeURIComponent(String(resolvedFormId))}/effective_form_schema`,
  );
  return { ...schema, form_id: resolvedFormId ?? undefined, published: published || { form_id: resolvedFormId ?? undefined } };
}

export async function getEffectiveFormData(
  formId: number | string,
  body: {
    asset_id: number | null;
    maintenance_event_id: number | null;
    values: Record<string, unknown>;
    /** Zuletzt vom Nutzer geändertes Feld (Antwortautomatik-Rücksprung). */
    changed_field_key?: string | null;
  },
): Promise<EffectiveFormSchema> {
  return api.post<EffectiveFormSchema>(`/forms/${encodeURIComponent(String(formId))}/effective_form_data`, body);
}

export async function writeFormValues(
  formId: number | string,
  body: {
    asset_id: number;
    maintenance_event_id: number;
    values: Record<string, unknown>;
    grade_manual?: string | number | null;
    grade_override_reason?: string | null;
    completion_status?: "in_progress" | "completed";
  },
): Promise<void> {
  await api.post(`/forms/${encodeURIComponent(String(formId))}/write`, body);
}

export async function getAssetSummary(
  typeKey: string,
  assetId: number | string,
  eventId?: number | string | null,
): Promise<AssetSummary> {
  const usp = new URLSearchParams();
  if (eventId != null) usp.set("maintenance_event_id", String(eventId));
  const qs = usp.toString();
  return api.get<AssetSummary>(
    `/maintenance-types/${encodeURIComponent(typeKey)}/assets/${encodeURIComponent(String(assetId))}/summary${qs ? `?${qs}` : ""}`,
  );
}

export async function searchMaterials(q: string): Promise<MaterialSuggestion[]> {
  const usp = new URLSearchParams({ q, tokens_all: "1" });
  const data = await api.get<unknown>(`/materials/suggestions?${usp.toString()}`);
  if (Array.isArray(data)) return data as MaterialSuggestion[];
  if (data && typeof data === "object" && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: MaterialSuggestion[] }).items;
  }
  return [];
}

/* ──────────────────────────────────────────────────────────────────────────
 * Objekte / Standorte / Events (Flow)
 * ──────────────────────────────────────────────────────────────────────── */

export interface AssetLookupResult {
  items?: Record<string, unknown>[];
  count?: number;
  mode?: string;
  [k: string]: unknown;
}

export async function lookupAssets(
  typeKey: string,
  params: Record<string, string | number | undefined> = {},
): Promise<AssetLookupResult> {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
  }
  const qs = usp.toString();
  return api.get<AssetLookupResult>(
    `/maintenance-types/${encodeURIComponent(typeKey)}/assets/lookup${qs ? `?${qs}` : ""}`,
  );
}

export async function startMaintenanceEvent(
  typeKey: string,
  payload: Record<string, unknown>,
): Promise<{ maintenance_event_id: number; reused_existing_event?: boolean }> {
  return api.post<{ maintenance_event_id: number; reused_existing_event?: boolean }>(
    `/maintenance-types/${encodeURIComponent(typeKey)}/maintenance-events/start`,
    { intent: "save", ...payload },
  );
}

export interface ContractAssetsBundle {
  project?: MaintenanceProject;
  contracts?: Array<{
    contract_id?: number;
    assets?: Record<string, unknown>[];
    locations?: ContractLocation[];
    require_plan_placement?: boolean;
    [k: string]: unknown;
  }>;
  [k: string]: unknown;
}

export async function getContractAssetsBundle(
  projectId: number | string,
  typeKey: string,
): Promise<ContractAssetsBundle> {
  const usp = new URLSearchParams({ project_id: String(projectId), type_key: typeKey });
  return api.get<ContractAssetsBundle>(`/maintenance/contracts?${usp.toString()}`);
}

export interface SubLocation {
  id?: number;
  name?: string | null;
  path_label?: string | null;
  path_segments?: string[];
  [k: string]: unknown;
}

export async function getSubLocations(heroAddressId: number | string): Promise<SubLocation[]> {
  const data = await api.get<{ items?: SubLocation[] }>(
    `/maintenance/locations/${encodeURIComponent(String(heroAddressId))}/sub-locations`,
  );
  return data?.items ?? [];
}

export async function createSubLocation(
  heroAddressId: number | string,
  payload: { name: string; parent_id?: number | null },
): Promise<SubLocation> {
  const data = await api.post<{ sub_location?: SubLocation } & SubLocation>(
    `/maintenance/locations/${encodeURIComponent(String(heroAddressId))}/sub-locations`,
    payload,
  );
  return data?.sub_location ?? data;
}

export async function deleteSubLocation(
  heroAddressId: number | string,
  subLocationId: number | string,
): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(
    `/maintenance/locations/${encodeURIComponent(String(heroAddressId))}/sub-locations/${encodeURIComponent(String(subLocationId))}`,
  );
}

export function countAssetsForSubLocation(
  assets: Array<{ sub_location_id?: number | null }>,
  subLocationId: number,
): number {
  return assets.filter((asset) => Number(asset.sub_location_id) === subLocationId).length;
}

export interface MaintenancePlan {
  id?: number;
  title?: string | null;
  version?: string | null;
  description?: string | null;
  filename?: string | null;
  file_size_bytes?: number | null;
  page?: number | null;
  page_number?: number | null;
  is_active?: boolean | null;
  source_file_type?: string | null;
  rendered_plan_image_url?: string | null;
  preview_url?: string | null;
  file_proxy_url?: string | null;
  marker_overlay_scale?: number | null;
  points?: Record<string, unknown>[];
  [k: string]: unknown;
}

export async function getSubLocationPlans(
  heroAddressId: number | string,
  subLocationId: number | string,
): Promise<MaintenancePlan[]> {
  const data = await api.get<{ plans?: MaintenancePlan[] }>(
    `/maintenance/locations/${encodeURIComponent(String(heroAddressId))}/sub-locations/${encodeURIComponent(String(subLocationId))}/plans`,
  );
  return data?.plans ?? [];
}

export async function uploadSubLocationPlan(
  heroAddressId: number | string,
  subLocationId: number | string,
  payload: {
    file: File;
    title: string;
    version?: string;
    description?: string;
    page?: number;
    is_active?: boolean;
  },
): Promise<MaintenancePlan> {
  const form = new FormData();
  form.set("file", payload.file);
  form.set("title", payload.title);
  form.set("version", payload.version ?? "");
  form.set("description", payload.description ?? "");
  form.set("page", String(Math.max(1, payload.page ?? 1)));
  form.set("is_active", payload.is_active ? "true" : "false");
  const data = await api.postForm<{ plan?: MaintenancePlan }>(
    `/maintenance/locations/${encodeURIComponent(String(heroAddressId))}/sub-locations/${encodeURIComponent(String(subLocationId))}/plans`,
    form,
  );
  if (!data?.plan) throw new Error("Plan-Antwort fehlt.");
  return data.plan;
}

export async function patchMaintenancePlan(
  planId: number | string,
  payload: Record<string, unknown>,
): Promise<MaintenancePlan> {
  const data = await api.patch<{ plan?: MaintenancePlan } & MaintenancePlan>(
    `/maintenance/plans/${encodeURIComponent(String(planId))}`,
    payload,
  );
  return data?.plan ?? data;
}

export async function deleteMaintenancePlan(planId: number | string): Promise<void> {
  await api.del(`/maintenance/plans/${encodeURIComponent(String(planId))}`);
}

export async function uploadMaintenancePlanLegacy(
  planId: number | string,
  payload: { file: File; page?: number },
): Promise<MaintenancePlan> {
  const form = new FormData();
  form.set("file", payload.file);
  form.set("page", String(Math.max(1, payload.page ?? 1)));
  const data = await api.postForm<{ plan?: MaintenancePlan }>(
    `/maintenance/plans/${encodeURIComponent(String(planId))}/legacy-plan`,
    form,
  );
  if (!data?.plan) throw new Error("Plan-Antwort fehlt.");
  return data.plan;
}

export async function deleteMaintenancePlanLegacy(planId: number | string): Promise<MaintenancePlan> {
  const data = await api.del<{ plan?: MaintenancePlan } & MaintenancePlan>(
    `/maintenance/plans/${encodeURIComponent(String(planId))}/legacy-plan`,
  );
  return data?.plan ?? data;
}

export async function uploadMaintenancePlanFireSafety(
  planId: number | string,
  payload: { file: File; page?: number },
): Promise<MaintenancePlan> {
  const form = new FormData();
  form.set("file", payload.file);
  form.set("page", String(Math.max(1, payload.page ?? 1)));
  const data = await api.postForm<{ plan?: MaintenancePlan }>(
    `/maintenance/plans/${encodeURIComponent(String(planId))}/fire-safety-plan`,
    form,
  );
  if (!data?.plan) throw new Error("Plan-Antwort fehlt.");
  return data.plan;
}

export async function deleteMaintenancePlanFireSafety(planId: number | string): Promise<MaintenancePlan> {
  const data = await api.del<{ plan?: MaintenancePlan } & MaintenancePlan>(
    `/maintenance/plans/${encodeURIComponent(String(planId))}/fire-safety-plan`,
  );
  return data?.plan ?? data;
}

export async function saveMaintenancePlanPoint(
  planId: number | string,
  assetId: number | string,
  payload: Record<string, unknown>,
): Promise<{ point?: Record<string, unknown> }> {
  return api.put<{ point?: Record<string, unknown> }>(
    `/maintenance/plans/${encodeURIComponent(String(planId))}/points/${encodeURIComponent(String(assetId))}`,
    payload,
  );
}

export interface ExternalIdLookup {
  exists: boolean;
  external_id: string;
  matched_previous_external_id?: string | null;
  asset: {
    asset_id?: number;
    external_id?: string;
    name?: string | null;
    hero_address_id?: number | null;
  } | null;
}

export async function getAssetByExternalId(
  typeKey: string,
  externalId: string,
  heroAddressId?: number | null,
): Promise<ExternalIdLookup> {
  const usp = new URLSearchParams({ external_id: externalId });
  if (heroAddressId && heroAddressId > 0) usp.set("hero_address_id", String(heroAddressId));
  try {
    const data = await api.get<ExternalIdLookup & { asset_id?: number }>(
      `/maintenance-types/${encodeURIComponent(typeKey)}/assets/by-external-id?${usp.toString()}`,
    );
    const nestedId = Number(data?.asset?.asset_id);
    const flatId = Number(data?.asset_id);
    const assetId = Number.isFinite(nestedId) && nestedId > 0 ? nestedId : Number.isFinite(flatId) && flatId > 0 ? flatId : null;
    if (!data?.exists && !assetId) {
      return { exists: false, external_id: externalId, asset: null };
    }
    return {
      exists: true,
      external_id: String(data?.external_id || externalId),
      matched_previous_external_id: data?.matched_previous_external_id ?? null,
      asset: {
        asset_id: assetId ?? undefined,
        external_id: data?.asset?.external_id ?? data?.external_id ?? externalId,
        name: data?.asset?.name ?? null,
        hero_address_id: data?.asset?.hero_address_id ?? null,
      },
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { exists: false, external_id: externalId, asset: null };
    }
    throw err;
  }
}

export async function getMaintenanceAsset(typeKey: string, assetId: number | string): Promise<AddressAsset> {
  const data = await api.get<AddressAsset & { asset?: AddressAsset }>(
    `/maintenance-types/${encodeURIComponent(typeKey)}/assets/${encodeURIComponent(String(assetId))}`,
  );
  const nested = data?.asset;
  if (
    nested &&
    typeof nested === "object" &&
    (nested.asset_id != null || nested.external_id != null || Object.prototype.hasOwnProperty.call(nested, "masterdata"))
  ) {
    return nested;
  }
  return data;
}

export async function createMaintenanceAsset(
  typeKey: string,
  payload: Record<string, unknown>,
): Promise<{ asset?: AddressAsset } & AddressAsset> {
  return api.post<{ asset?: AddressAsset } & AddressAsset>(
    `/maintenance-types/${encodeURIComponent(typeKey)}/assets`,
    payload,
  );
}

export async function updateMaintenanceAsset(
  typeKey: string,
  assetId: number | string,
  payload: Record<string, unknown>,
): Promise<{ asset?: AddressAsset } & AddressAsset> {
  return api.put<{ asset?: AddressAsset } & AddressAsset>(
    `/maintenance-types/${encodeURIComponent(typeKey)}/assets/${encodeURIComponent(String(assetId))}`,
    payload,
  );
}

export async function deleteMaintenanceAsset(
  typeKey: string,
  assetId: number | string,
): Promise<{ ok?: boolean; already_deleted?: boolean; asset_id?: number; status?: string }> {
  return api.del<{ ok?: boolean; already_deleted?: boolean; asset_id?: number; status?: string }>(
    `/maintenance-types/${encodeURIComponent(typeKey)}/assets/${encodeURIComponent(String(assetId))}`,
  );
}

export async function searchProjects(q: string, limit = 20): Promise<MaintenanceProject[]> {
  const usp = new URLSearchParams({ q, limit: String(limit) });
  const data = await api.get<unknown>(`/maintenance/projects/search?${usp.toString()}`);
  if (Array.isArray(data)) return data as MaintenanceProject[];
  if (data && typeof data === "object" && Array.isArray((data as { projects?: unknown }).projects)) {
    return (data as { projects: MaintenanceProject[] }).projects;
  }
  return [];
}

export interface HeroLocation {
  hero_address_id?: number;
  id?: number;
  name?: string | null;
  label?: string | null;
  city?: string | null;
  [k: string]: unknown;
}

export async function getHeroLocations(
  params: { hero_address_id?: number | string; query?: string; limit?: number } = {},
): Promise<HeroLocation[]> {
  const usp = new URLSearchParams();
  if (params.hero_address_id) usp.set("hero_address_id", String(params.hero_address_id));
  if (params.query) usp.set("query", params.query);
  if (params.limit) usp.set("limit", String(params.limit));
  const qs = usp.toString();
  const data = await api.get<unknown>(`/maintenance/hero-locations${qs ? `?${qs}` : ""}`);
  if (Array.isArray(data)) return data as HeroLocation[];
  if (data && typeof data === "object" && Array.isArray((data as { locations?: unknown }).locations)) {
    return (data as { locations: HeroLocation[] }).locations;
  }
  return [];
}

export interface AssetLink {
  other_asset?: {
    asset_id?: number;
    name?: string | null;
    external_id?: string | null;
    maintenance_type_key?: string | null;
    maintenance_type_name?: string | null;
    [k: string]: unknown;
  };
  [k: string]: unknown;
}

export interface AddressAsset {
  asset_id?: number;
  id?: number;
  name?: string | null;
  external_id?: string | null;
  type_key?: string | null;
  maintenance_type_key?: string | null;
  maintenance_type_name?: string | null;
  sub_location_name?: string | null;
  sub_location_id?: number | string | null;
  last_performed_at?: string | null;
  links?: AssetLink[];
  [k: string]: unknown;
}

export async function getAddressAssets(
  addressId: number | string,
  typeKeys: string[] = [],
): Promise<AddressAsset[]> {
  const usp = new URLSearchParams({ include_links: "true" });
  if (typeKeys.length) usp.set("type_keys", typeKeys.join(","));
  const data = await api.get<{ assets?: AddressAsset[] }>(
    `/addresses/${encodeURIComponent(String(addressId))}/assets?${usp.toString()}`,
  );
  return data?.assets ?? [];
}
